/**
 * AddTaskModal Component
 * Modal for creating new tasks
 * REFACTORED: Minimal data entry with auto-generated titles
 * Uses unified category system for consistent categorization
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Banknote, Tag, Bell, Link as LinkIcon, Sparkles, Check, X as XIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn, toTitleCase } from '@/lib/utils';
import {
  TASK_STATUS,
  TASK_PRIORITY,
  LINKED_TYPE,
  RECURRENCE_TYPE
} from '@/services/finTasksService';
import { useTodoLists } from '@/hooks/useTodoLists';
import UnifiedCategorySelector from '@/components/UnifiedCategorySelector';
import { EnvelopeSelectorCompact } from '@/components/EnvelopeSelector';
import { buildAutoTaskTitle, isAutoGeneratedTitle } from '@/utils/taskTitles';
import { CONTEXT_TYPE } from '@/utils/contextLinking';
import ContextAutocomplete from './ContextAutocomplete';
import { fetchContextOptions, getSmartSuggestions } from '@/utils/contextDataService';
import { insightsAPI } from '@/services/api';
import { auth } from '@/config/firebase';
import { findCategoryById } from '@/constants/unifiedCategories';

// Predefined tags for quick selection
const REGULAR_TASK_TAGS = [
  { label: 'Urgent', color: '#ef4444' },
  { label: 'Home', color: '#10b981' },
  { label: 'Work', color: '#3b82f6' },
  { label: 'Personal', color: '#8b5cf6' },
  { label: 'Family', color: '#f59e0b' },
  { label: 'Health', color: '#ec4899' },
  { label: 'Shopping', color: '#06b6d4' },
  { label: 'Errands', color: '#84cc16' },
];

const FINANCIAL_TASK_TAGS = [
  { label: 'Recurring', color: '#6366f1' },
  { label: 'One-time', color: '#14b8a6' },
  { label: 'Bill', color: '#f97316' },
  { label: 'Savings', color: '#22c55e' },
  { label: 'Investment', color: '#0ea5e9' },
  { label: 'Tax', color: '#a855f7' },
  { label: 'Insurance', color: '#64748b' },
  { label: 'Debt', color: '#ef4444' },
];

export default function AddTaskModal({
  isOpen,
  onClose,
  onSubmit,
  defaultListId = null,
  prefilledData = {}
}) {
  const { lists, loading: listsLoading, addList } = useTodoLists();
  
  const [formData, setFormData] = useState({
    listId: defaultListId || '',
    taskType: 'regular',
    title: '',
    description: '',
    priority: TASK_PRIORITY.MEDIUM,
    dueDate: '',
    reminderAt: '',
    isFinancialTask: false,
    estimatedAmount: '',
    currency: 'GBP',
    linkedType: LINKED_TYPE.NONE,
    linkedRef: '',
    linkedLabel: '',
    contextLabel: '',
    contextType: 'none',
    contextId: '',
    tags: [],
    recurrenceType: RECURRENCE_TYPE.NONE,
    // FINLIST ENVELOPE SYSTEM: Link tasks to budget envelopes and goals
    linkedEnvelopeId: '',
    goalId: ''
  });

  // Unified category state
  const [categoryState, setCategoryState] = useState({
    categoryId: null,
    subcategoryId: null,
    customCategory: "",
    customSubcategory: ""
  });

  const [tagInput, setTagInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [isTitleManuallyEdited, setIsTitleManuallyEdited] = useState(false);
  
  // AI description enhancement state
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  
  // Phase 2.1: Autocomplete state
  const [contextOptions, setContextOptions] = useState([]);
  const [loadingContextOptions, setLoadingContextOptions] = useState(false);

  // Prefill form with provided data
  useEffect(() => {
    if (prefilledData && Object.keys(prefilledData).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...prefilledData,
        listId: prefilledData.listId || defaultListId || prev.listId
      }));
    }
  }, [prefilledData, defaultListId]);

  // Auto-select default list when lists are loaded
  useEffect(() => {
    // Skip if we already have a listId selected
    if (formData.listId) return;
    
    // Skip if lists are still loading
    if (listsLoading) return;

    // Select the default list or first list when available
    if (lists.length > 0) {
      const defaultList = lists.find(l => l.isDefault) || lists[0];
      setFormData(prev => ({ ...prev, listId: defaultList.id }));
    }
  }, [lists, listsLoading, formData.listId]);

  // Auto-generate title when taskType, category, or subcategory changes (unless manually edited)
  useEffect(() => {
    if (!isTitleManuallyEdited && formData.category && formData.subcategory) {
      const autoTitle = buildAutoTaskTitle({
        taskType: formData.taskType,
        category: formData.category,
        subcategory: formData.subcategory,
        subcategoryName: formData.subcategoryName // Pass display name for better titles
      });
      
      if (autoTitle) {
        setFormData(prev => ({ ...prev, title: autoTitle }));
      }
    }
  }, [formData.taskType, formData.category, formData.subcategory, formData.subcategoryName, isTitleManuallyEdited]);

  // Sync isFinancialTask with taskType
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      isFinancialTask: prev.taskType === 'financial'
    }));
  }, [formData.taskType]);

  // Phase 2.1: Fetch context options when contextType changes
  useEffect(() => {
    const loadContextOptions = async () => {
      if (formData.contextType === 'none' || !formData.contextType) {
        setContextOptions([]);
        return;
      }

      const userId = auth.currentUser?.uid;
      if (!userId) return;

      setLoadingContextOptions(true);
      
      try {
        // Get smart suggestions based on task category
        const options = await getSmartSuggestions(userId, {
          category: formData.category,
          subcategory: formData.subcategory,
        }, formData.contextType);
        
        setContextOptions(options);
      } catch (error) {
        console.error('[ADD TASK] Error loading context options:', error);
        toast.error('Failed to load options');
        setContextOptions([]);
      } finally {
        setLoadingContextOptions(false);
      }
    };

    loadContextOptions();
  }, [formData.contextType, formData.category, formData.subcategory]);

  // AI description enhancement when description is entered
  useEffect(() => {
    const getSuggestion = async () => {
      if (formData.description.trim().length > 5 && formData.category && !suggestionDismissed) {
        setLoadingSuggestion(true);
        try {
          const categoryId = getCategoryIdForAI(formData.category);
          const result = await insightsAPI.enhanceDescription(
            formData.description, 
            categoryId, 
            formData.subcategory, 
            'task'
          );
          
          // Backend returns { success: true, data: { enhanced, original, confidence, category } }
          if (result.success && result.data?.enhanced && result.data.enhanced !== formData.description.trim()) {
            setAiSuggestion(result.data);
          } else {
            setAiSuggestion(null);
          }
        } catch (error) {
          console.error('AI suggestion error:', error);
          setAiSuggestion(null);
        } finally {
          setLoadingSuggestion(false);
        }
      } else {
        setAiSuggestion(null);
      }
    };

    const debounce = setTimeout(getSuggestion, 800);
    return () => clearTimeout(debounce);
  }, [formData.description, formData.category, formData.subcategory, suggestionDismissed]);

  // Unified: is custom category selected
  const isCustom = categoryState.categoryId === "__custom__";

  // Handle task type change
  const handleTaskTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      taskType: type,
      isFinancialTask: type === 'financial'
    }));
  };

  // Remove legacy handleCategoryChange

  // Handle title change (mark as manually edited if user types)
  const handleTitleChange = (e) => {
    const newTitle = toTitleCase(e.target.value);
    setFormData(prev => ({ ...prev, title: newTitle }));
    setIsTitleManuallyEdited(true);
  };

  // Map task categories to backend category IDs for AI
  const getCategoryIdForAI = (taskCategory) => {
    const categoryMap = {
      'bills-utilities': 'cat_bills',
      'savings-investments': 'cat_savings',
      'loan-debt': 'cat_debt',
      'income-earnings': 'cat_income',
      'shopping-purchases': 'cat_shopping',
      'business-work': 'cat_business',
      'subscriptions-renewals': 'cat_subscription',
      'transportation-fuel': 'cat_transportation',
      'rent-housing': 'cat_housing',
      'insurance': 'cat_insurance',
      'tax-compliance': 'cat_taxes',
      'medical-health': 'cat_healthcare',
      'education': 'cat_education',
      'family-personal': 'cat_personal',
      'entertainment': 'cat_entertainment'
    };
    return categoryMap[taskCategory] || 'cat_other';
  };

  // Accept AI suggestion for description
  const acceptSuggestion = () => {
    if (aiSuggestion?.enhanced) {
      setFormData(prev => ({ ...prev, description: aiSuggestion.enhanced }));
      setAiSuggestion(null);
      setSuggestionDismissed(true);
    }
  };

  // Dismiss AI suggestion
  const dismissSuggestion = () => {
    setAiSuggestion(null);
    setSuggestionDismissed(true);
  };

  // Phase 2.1: Handle context option selection from autocomplete
  const handleContextSelect = (option) => {
    if (!option) {
      // Cleared selection
      setFormData(prev => ({
        ...prev,
        contextId: '',
        contextLabel: ''
      }));
      return;
    }

    // Populate contextId and contextLabel from selected option
    setFormData(prev => ({
      ...prev,
      contextId: option.id,
      contextLabel: option.name
    }));
  };

  // Handle list selection change
  const handleListChange = (e) => {
    const value = e.target.value;
    
    if (value === '__CREATE_NEW__') {
      setShowCreateListModal(true);
    } else {
      setFormData({ ...formData, listId: value });
    }
  };

  // Handle creating a new list
  const handleCreateNewList = async () => {
    if (!newListName.trim()) {
      toast.error('Please enter a list name');
      return;
    }

    try {
      const newList = await addList({
        title: newListName.trim(),
        isDefault: false,
        sortOrder: lists.length + 1,
      });
      
      if (newList) {
        setFormData({ ...formData, listId: newList.id });
        toast.success(`List "${newListName}" created!`);
        setShowCreateListModal(false);
        setNewListName('');
      }
    } catch (error) {
      console.error('Failed to create list:', error);
      toast.error('Could not create list');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields: Category & Subcategory
    if (!categoryState.categoryId) {
      toast.error('Please select a category');
      return;
    }

    // Validate subcategory or custom fields
    if (isCustom) {
      if (!categoryState.customCategory.trim() || !categoryState.customSubcategory.trim()) {
        toast.error('Please enter both custom category and subcategory');
        return;
      }
    } else {
      if (!categoryState.subcategoryId) {
        toast.error('Please select a subcategory');
        return;
      }
    }

    // Validate amount for financial tasks
    if (formData.taskType === 'financial' && !formData.estimatedAmount) {
      toast.error('Please enter an amount for financial tasks');
      return;
    }

    // Ensure title is set - fallback to auto-generated if somehow empty
    let finalTitle = formData.title.trim();
    if (!finalTitle) {
      // Helper to format category IDs into readable names
      const formatId = (id) => id ? toTitleCase(id.replace(/[-_]/g, ' ')) : '';
      finalTitle = buildAutoTaskTitle({
        taskType: formData.taskType,
        category: isCustom ? categoryState.customCategory : categoryState.categoryId,
        subcategory: isCustom ? categoryState.customSubcategory : categoryState.subcategoryId
      }) || `${formatId(categoryState.categoryId)} – ${formatId(categoryState.subcategoryId)}`.replace(' – ', ' ').trim();
    }

    if (!finalTitle) {
      toast.error('Please enter a task title');
      return;
    }

    // Auto-select or create default list if none selected
    let listId = formData.listId;
    if (!listId) {
      if (lists.length > 0) {
        listId = lists.find(l => l.isDefault)?.id || lists[0].id;
      } else {
        // Create a default list if none exists
        const defaultList = await addList({ title: 'My Tasks', isDefault: true });
        listId = defaultList.id;
      }
    }

    setIsSubmitting(true);

    try {
      const taskData = {
        ...formData,
        listId,
        title: finalTitle,
        description: formData.description.trim(),
        categoryId: categoryState.categoryId,
        subcategoryId: categoryState.subcategoryId,
        customCategory: categoryState.customCategory,
        customSubcategory: categoryState.customSubcategory,
        estimatedAmount: formData.estimatedAmount ? parseFloat(formData.estimatedAmount) : null,
        // FINLIST ENVELOPE SYSTEM: Include envelope and goal links
        linkedEnvelopeId: formData.linkedEnvelopeId || null,
        goalId: formData.goalId || null,
        recurrence: {
          type: formData.recurrenceType
        }
      };

      // Remove empty fields (sanitize for Firestore)
      Object.keys(taskData).forEach(key => {
        if (taskData[key] === undefined || taskData[key] === null || taskData[key] === "") {
          delete taskData[key];
        }
      });

      await onSubmit(taskData);
      toast.success('Task created successfully!');
      onClose();
      // Reset form
      setFormData({
        listId: defaultListId || '',
        taskType: 'regular',
        title: '',
        description: '',
        priority: TASK_PRIORITY.MEDIUM,
        dueDate: '',
        reminderAt: '',
        isFinancialTask: false,
        estimatedAmount: '',
        currency: 'GBP',
        linkedType: LINKED_TYPE.NONE,
        linkedRef: '',
        linkedLabel: '',
        contextLabel: '',
        contextType: 'none',
        contextId: '',
        tags: [],
        recurrenceType: RECURRENCE_TYPE.NONE,
        linkedEnvelopeId: '',
        goalId: ''
      });
      setCategoryState({ categoryId: null, subcategoryId: null, customCategory: "", customSubcategory: "" });
      setTagInput('');
      setIsTitleManuallyEdited(false);
      setSuggestionDismissed(false);
      setAiSuggestion(null);
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    console.log('addTag called:', { tag, currentTags: formData.tags });
    
    if (!tag) {
      toast.error('Please enter a tag');
      return;
    }
    
    if (formData.tags.includes(tag)) {
      toast.error('Tag already added');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, tag]
    }));
    setTagInput('');
    toast.success(`Tag "${tag}" added`);
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove)
    }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-white dark:bg-slate-800 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-slate-800 border-b-2 border-gray-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between rounded-t-3xl z-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Add New Task</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
              aria-label="Close modal"
            >
              <X size={24} className="text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* List Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                List *
              </label>
              <div className="relative">
                <select
                  value={formData.listId}
                  onChange={handleListChange}
                  disabled={listsLoading}
                  className="w-full px-4 py-3 pr-10 bg-white dark:bg-slate-700 text-gray-900 dark:text-white border-2 border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-[#006D77] dark:focus:border-teal-500 focus:ring-4 focus:ring-[#006D77]/10 dark:focus:ring-teal-500/10 transition-all appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                >
                  <option value="">
                    {listsLoading ? 'Loading lists...' : 'Select a list'}
                  </option>
                  {lists.map(list => (
                    <option key={list.id} value={list.id}>
                      {list.icon ? `${list.icon} ` : ''}{list.title}
                    </option>
                  ))}
                  <option value="__CREATE_NEW__" className="font-semibold text-[#006D77]">
                    ➕ Create New List...
                  </option>
                </select>
                {/* Dropdown Arrow */}
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg 
                    className={cn(
                      "w-5 h-5 text-gray-400 dark:text-gray-500 transition-opacity",
                      listsLoading && "opacity-30"
                    )} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              {listsLoading && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 animate-pulse">
                  Loading your lists...
                </p>
              )}
            </div>

            {/* Task Type Toggle - Financial vs Regular */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Task Type
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleTaskTypeChange('regular')}
                  className={cn(
                    'flex-1 px-4 py-3 rounded-xl text-sm font-semibold transition-all border-2',
                    formData.taskType === 'regular'
                      ? 'bg-[#006D77] dark:bg-teal-600 text-white border-[#006D77] dark:border-teal-600 shadow-md'
                      : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                  )}
                >
                  📋 Regular Task
                </button>
                <button
                  type="button"
                  onClick={() => handleTaskTypeChange('financial')}
                  className={cn(
                    'flex-1 px-4 py-3 rounded-xl text-sm font-semibold transition-all border-2',
                    formData.taskType === 'financial'
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-md'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                  )}
                >
                  💰 Financial Task
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1.5">
                {formData.taskType === 'financial' 
                  ? 'Track expenses and link to your budget'
                  : 'Simple task or reminder without financial tracking'}
              </p>
            </div>

            {/* Unified Category Selector */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Category *
              </label>
              <UnifiedCategorySelector
                module="tasks"
                value={categoryState}
                onChange={(newState) => {
                  setCategoryState(newState);
                  
                  // Sync with formData for auto-title generation
                  const category = findCategoryById(newState.categoryId);
                  
                  // For auto-title generation, use IDs (or custom values)
                  const categoryId = newState.customCategory || newState.categoryId || '';
                  const subcategoryId = newState.customSubcategory || newState.subcategoryId || '';
                  
                  // Get display names for fallback title
                  const categoryName = newState.customCategory || category?.name || '';
                  let subcategoryName = '';
                  
                  if (newState.customSubcategory) {
                    subcategoryName = newState.customSubcategory;
                  } else if (newState.subcategoryId && category?.subcategories) {
                    const subcat = category.subcategories.find(s => s.id === newState.subcategoryId);
                    subcategoryName = subcat?.name || '';
                  }
                  
                  setFormData(prev => ({
                    ...prev,
                    category: categoryId,
                    subcategory: subcategoryId,
                    // Store display names for fallback
                    categoryName: categoryName,
                    subcategoryName: subcategoryName
                  }));
                  
                  // Auto-fill description if empty
                  if (!formData.description.trim() && category?.description) {
                    setFormData(prev => ({ ...prev, description: category.description }));
                  }
                }}
                allowSubcategory={true}
                allowCustom={true}
                placeholder="Select task category"
              />
            </div>

            {/* Subcategory and custom fields now handled by UnifiedCategorySelector */}

            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Task Title * {!isTitleManuallyEdited && formData.title && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">(auto-generated, click to edit)</span>
                )}
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={handleTitleChange}
                placeholder="e.g., Pay electricity bill"
                className="w-full px-4 py-3 bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-[#006D77] focus:ring-4 focus:ring-[#006D77]/10 transition-all"
                required
              />
            </div>
            {/* Context Label - Link to Budget/Category/List */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <LinkIcon size={14} className="text-purple-600" />
                Link to (Optional)
              </label>
              
              {/* Context Type Selector */}
              <div className="flex gap-2 mb-3">
                {[
                  { value: 'none', label: 'No Link', icon: '⊘' },
                  { value: CONTEXT_TYPE.BUDGET, label: 'Budget', icon: '💰' },
                  { value: CONTEXT_TYPE.CATEGORY, label: 'Category', icon: '📁' },
                  { value: CONTEXT_TYPE.TRANSACTION, label: 'Transaction', icon: '🧾' },
                  { value: CONTEXT_TYPE.LIST, label: 'List', icon: '🛒' }
                ].map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, contextType: type.value, contextId: '', contextLabel: '' })}
                    className={cn(
                      'px-3 py-2 rounded-lg text-xs font-semibold transition-all',
                      formData.contextType === type.value
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    )}
                  >
                    {type.icon} {type.label}
                  </button>
                ))}
              </div>

              {/* Context Autocomplete - only show if a context type is selected */}
              {formData.contextType && formData.contextType !== 'none' && (
                <ContextAutocomplete
                  options={contextOptions}
                  loading={loadingContextOptions}
                  onSelect={handleContextSelect}
                  selectedId={formData.contextId}
                  placeholder={`Search ${formData.contextType}...`}
                />
              )}
            </div>

            {/* Financial Task Fields - Amount & Currency */}
            {formData.isFinancialTask && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    <Banknote size={16} />
                    Estimated Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.estimatedAmount}
                    onChange={(e) => setFormData({ ...formData, estimatedAmount: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-4 py-3 bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-[#006D77] focus:ring-4 focus:ring-[#006D77]/10 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Currency
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-4 py-3 bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-[#006D77] focus:ring-4 focus:ring-[#006D77]/10 transition-all"
                  >
                    <option value="GBP">GBP (£)</option>
                    <option value="NGN">NGN (₦)</option>
                  </select>
                </div>
              </div>
            )}

            {/* FINLIST ENVELOPE SYSTEM: Link to Budget Envelope */}
            {formData.isFinancialTask && (
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  <LinkIcon size={16} />
                  Budget Envelope <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                </label>
                <EnvelopeSelectorCompact
                  selectedEnvelopeId={formData.linkedEnvelopeId || null}
                  onChange={(envelope) => setFormData({ ...formData, linkedEnvelopeId: envelope?.id || '' })}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Link this task to a budget envelope for expense tracking
                </p>
              </div>
            )}

            {/* Due Date & Reminder - Always visible for all task types */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Due Date */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  <Calendar size={16} />
                  Due Date
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  className="w-full px-4 py-3 bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-[#006D77] focus:ring-4 focus:ring-[#006D77]/10 transition-all"
                />
              </div>

              {/* Reminder */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  <Bell size={16} />
                  Reminder
                </label>
                <input
                  type="datetime-local"
                  value={formData.reminderAt}
                  onChange={(e) => setFormData({ ...formData, reminderAt: e.target.value })}
                  className="w-full px-4 py-3 bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-[#006D77] focus:ring-4 focus:ring-[#006D77]/10 transition-all"
                />
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <Tag size={16} />
                Tags
              </label>
              
              {/* Quick Tag Presets */}
              <div className="mb-3">
                <p className="text-xs text-gray-500 mb-2">
                  Quick add {formData.taskType === 'financial' ? 'financial' : 'regular'} tags:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {(formData.taskType === 'financial' ? FINANCIAL_TASK_TAGS : REGULAR_TASK_TAGS).map((tagPreset) => {
                    const isSelected = formData.tags.includes(tagPreset.label);
                    return (
                      <button
                        key={tagPreset.label}
                        type="button"
                        onClick={() => {
                          if (isSelected) {
                            removeTag(tagPreset.label);
                          } else {
                            setFormData(prev => ({
                              ...prev,
                              tags: [...prev.tags, tagPreset.label]
                            }));
                          }
                        }}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                          isSelected 
                            ? 'ring-2 ring-offset-1 ring-gray-400' 
                            : 'opacity-70 hover:opacity-100'
                        }`}
                        style={{ 
                          backgroundColor: isSelected ? tagPreset.color : `${tagPreset.color}20`,
                          color: isSelected ? 'white' : tagPreset.color
                        }}
                      >
                        {tagPreset.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Tag Input */}
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder="Or add a custom tag..."
                  className="flex-1 px-4 py-2.5 bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-[#006D77] focus:ring-4 focus:ring-[#006D77]/10 transition-all text-sm"
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors text-sm"
                >
                  Add
                </button>
              </div>
              
              {/* Selected Tags Display */}
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  <span className="text-xs text-gray-500 mr-1">Selected:</span>
                  {formData.tags.map((tag, index) => {
                    // Find if this is a preset tag to get its color
                    const presetTag = [...REGULAR_TASK_TAGS, ...FINANCIAL_TASK_TAGS].find(t => t.label === tag);
                    return (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                        style={{ 
                          backgroundColor: presetTag ? presetTag.color : '#6b7280',
                          color: 'white'
                        }}
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-6 py-3.5 bg-gradient-to-r from-[#006D77] to-[#118AB2] text-white rounded-xl font-bold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Creating...' : 'Create Task'}
              </button>
            </div>
          </form>
        </motion.div>

        {/* Create New List Modal */}
        <AnimatePresence>
          {showCreateListModal && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => {
                  setShowCreateListModal(false);
                  setNewListName('');
                }}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm z-10"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md z-20"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-4">Create New List</h3>
                <input
                  type="text"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateNewList();
                    }
                  }}
                  placeholder="Enter list name..."
                  className="w-full px-4 py-3 bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-[#006D77] focus:ring-4 focus:ring-[#006D77]/10 transition-all mb-4"
                  autoFocus
                />
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateListModal(false);
                      setNewListName('');
                    }}
                    className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateNewList}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#006D77] to-[#118AB2] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                  >
                    Create List
                  </button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </AnimatePresence>
  );
}
